import { Controller } from "@hotwired/stimulus";
import { CalendarScheduler } from "../calendar";

// The only file that knows about both the calendar package and Rails.
// The package emits; this controller persists.
function formatLength(hours) {
  if (!Number.isFinite(hours)) return "—";
  if (hours < 24) return `${hours} ${hours === 1 ? "hour" : "hours"}`;
  const days = hours / 24;
  if (Number.isInteger(days)) return `${days} ${days === 1 ? "day" : "days"}`;
  return `${hours} hours`;
}

export default class extends Controller {
  static targets = [
    "calendar", "rangeLabel", "vehicleFilter",
    "weekButton", "fortnightButton", "monthButton",
    "panel", "pendingBox", "pendingSummary", "pendingPrice", "confirmButton"
  ];

  static values = {
    date: String,
    view: String,
    span: Number,
    vehicleId: String,
    eventsPath: String
  };

  connect() {
    this.calendar = new CalendarScheduler({
      element: this.calendarTarget,
      date: this.dateValue,
      view: this.viewValue,
      span: this.spanValue,
      resources: [],
      events: []
    });

    this._subscribe("date-change", () => this.reload());
    this._subscribe("view-change", () => this.reload());
    this._subscribe("span-change", () => this.reload());
    this._subscribe("event-click", (e) => this.openInspector(e.detail));
    this._subscribe("range-select", (e) => this.startCreate(e.detail));
    this._subscribe("event-drag", (e) => this.previewDrag(e.detail));
    this._subscribe("event-move", (e) => this.commitOrConfirm(e.detail));
    this._subscribe("event-resize", (e) => this.commitOrConfirm(e.detail));
    this._subscribe("drag-cancel", (e) => this.cancelPending(e.detail));
    // "+N more" in the month grid: open the week it belongs to, where each
    // vehicle has its own row and nothing has to overflow.
    this._subscribe("overflow-open", (e) => this.openWeek(e.detail.startDate));
    this.pending = null;

    this.reload();
  }

  disconnect() {
    // Otherwise a debounced preview fetch can resolve and re-arm the pending
    // box against a destroyed calendar after a Turbo navigation (M9).
    clearTimeout(this._previewTimer);
    this.calendar?.destroy();
  }

  // ---- toolbar ----

  previous() { this.calendar.shiftDate(-1); }
  next() { this.calendar.shiftDate(1); }
  today() { this.calendar.goToToday(); }

  showWeek() { this.calendar.setSpan(1); this.calendar.setView("week"); this.reload(); }
  showFortnight() { this.calendar.setSpan(2); this.calendar.setView("week"); this.reload(); }
  showMonth() { this.calendar.setView("month"); this.reload(); }

  // A single week, not a fortnight: the point is to give the crowded days as
  // much column width as possible.
  openWeek(startDate) {
    this.calendar.setDate(startDate);
    this.calendar.setSpan(1);
    this.calendar.setView("week");
    this.reload();
  }

  filterChanged() {
    this.vehicleIdValue = this.vehicleFilterTarget.value;
    this.reload();
  }

  // ---- data ----

  async reload() {
    const { startDate, endDate } = this.visibleRange();
    const params = new URLSearchParams({ start_date: startDate, end_date: endDate });
    if (this.vehicleIdValue) params.set("vehicle_id", this.vehicleIdValue);

    const result = await this.fetchJson(`${this.eventsPathValue}?${params}`, {
      headers: { Accept: "application/json" }
    });

    // This is the revert mechanism for every failure path in this controller —
    // a refused drag toasts its conflict and then calls reload() to put the bar
    // back. Returning silently here leaves the bar at the position the server
    // refused, and the conflict toast expires on top of a board showing a
    // double-booking that does not exist.
    if (!result.ok) {
      this.showError(result.signedOut
        ? "Your session expired. Sign in again to continue."
        : "Could not load the calendar. Refresh the page.");
      return;
    }

    const { resources, events } = result.body;
    this.calendar.setData({ resources, events });

    this.updateRangeLabel(startDate, endDate);
    this.updateViewButtons();
    this.syncUrl();
  }

  // Reflects which of Week / 2 weeks / Month is current. Called from reload()
  // so it stays correct after every view/span change (showWeek/Fortnight/Month
  // all call reload()) as well as on initial load.
  updateViewButtons() {
    const active = this.calendar.view === "month" ? "monthButton"
      : this.calendar.span === 2 ? "fortnightButton"
      : "weekButton";

    [ "weekButton", "fortnightButton", "monthButton" ].forEach((name) => {
      if (!this[`has${name[0].toUpperCase()}${name.slice(1)}Target`]) return;
      const button = this[`${name}Target`];
      const isActive = name === active;
      button.classList.toggle("bg-brand", isActive);
      button.classList.toggle("text-white", isActive);
      button.classList.toggle("bg-white", !isActive);
      button.classList.toggle("text-ink", !isActive);
    });
  }

  // In month view the range is the GRID, not the calendar month — the grid
  // pads with adjacent-month days, and a booking on Jul 30 must arrive or the
  // first row renders empty.
  visibleRange() {
    const anchor = this.parseDate(this.calendar.date);

    if (this.calendar.view === "month") {
      const first = new Date(anchor.getFullYear(), anchor.getMonth(), 1);
      const start = this.mondayOf(first);
      // The grid is ALWAYS six rows of seven (MonthView's TOTAL_CELLS = 42),
      // so the range is 42 days from the Monday on or before the 1st. Deriving
      // the end from the month's last day instead would come up a week short
      // whenever the last day falls in row five — February 2027 starts on a
      // Monday and ends on a Sunday, so that arithmetic yields 28 days while
      // the grid still renders 42, and every bar in the last two rows would
      // silently fail to load.
      const end = new Date(start);
      end.setDate(end.getDate() + 41);
      return { startDate: this.format(start), endDate: this.format(end) };
    }

    const start = this.mondayOf(anchor);
    const end = new Date(start);
    end.setDate(end.getDate() + 7 * this.calendar.span - 1);
    return { startDate: this.format(start), endDate: this.format(end) };
  }

  updateRangeLabel(startDate, endDate) {
    const opts = { month: "short", day: "numeric" };
    const start = this.parseDate(startDate).toLocaleDateString(undefined, opts);
    const end = this.parseDate(endDate).toLocaleDateString(undefined, opts);
    this.rangeLabelTarget.textContent = `${start} – ${end}`;
  }

  syncUrl() {
    const params = new URLSearchParams({
      date: this.calendar.date,
      view: this.calendar.view,
      span: String(this.calendar.span)
    });
    if (this.vehicleIdValue) params.set("vehicle_id", this.vehicleIdValue);
    history.replaceState({}, "", `${location.pathname}?${params}`);
  }

  // ---- inspector ----

  async openInspector({ id }) {
    await this.fetchInspectorFrame(`/app/bookings/${id}/inspector`);
    this.panelBookingId = String(id);
    this.panelTarget.classList.remove("hidden");
  }

  async startCreate({ resourceId, startDate, endDate }) {
    const params = new URLSearchParams({ start_date: startDate, end_date: endDate });
    if (resourceId) params.set("vehicle_id", resourceId);
    await this.fetchInspectorFrame(`/app/bookings/new/inspector?${params}`);
    this.panelBookingId = null;
    this.panelTarget.classList.remove("hidden");
  }

  closeInspector() {
    this.panelTarget.classList.add("hidden");
    this.panelBookingId = null;
    this.pending = null;
  }

  // Replaces the inspector frame's content in place, without touching the
  // panel's open/closed state. Shared by openInspector/startCreate and by the
  // post-commit refresh below — extracted so both stay on one code path.
  //
  // The replacement assigns to the frame's own outerHTML, so writing a Rails
  // error page or the sign-in form in here REMOVES the turbo-frame element —
  // and every later open then throws on a null querySelector, leaving the panel
  // dead for the lifetime of the page. One failed fetch used to cost the
  // operator the panel until they reloaded.
  async fetchInspectorFrame(url) {
    const frame = this.panelTarget.querySelector("turbo-frame");
    if (!frame) return false;

    let html;
    try {
      const response = await fetch(url, { headers: { Accept: "text/html" } });
      if (!response.ok) throw new Error(`inspector responded ${response.status}`);
      html = await response.text();
    } catch {
      this.showError("Could not load that booking's details.");
      return false;
    }

    // Only replace the frame with something that is itself a frame; anything
    // else would take the element with it.
    if (!html.includes("<turbo-frame")) {
      this.showError("Could not load that booking's details.");
      return false;
    }

    frame.outerHTML = html;
    return true;
  }

  // Called after a commit succeeds, so the panel's form fields (dates,
  // lock_version) never sit stale behind a reschedule the operator just
  // approved. Only refreshes when the panel is already open on THIS booking —
  // never pops it open, which would undo the silent-commit behaviour a pure
  // move depends on.
  async refreshInspectorIfOpen(id) {
    if (this.panelTarget.classList.contains("hidden")) return;
    if (this.panelBookingId !== String(id)) return;
    await this.fetchInspectorFrame(`/app/bookings/${id}/inspector`);
  }

  // ---- drag ----

  // Fires continuously during a drag. Debounced so a fast drag doesn't
  // hammer the preview endpoint.
  previewDrag({ id, pending }) {
    clearTimeout(this._previewTimer);
    this._previewTimer = setTimeout(() => this.fetchPreview(id, pending), 120);
  }

  // Every preview carries the token that was current when it was fired.
  // Clearing the debounce timer cannot recall a request already on the wire:
  // without this, a preview fired mid-drag resolves AFTER the drop commits and
  // re-arms the Confirm box for a change the operator never made — and
  // confirming it would PATCH the booking to that intermediate position.
  get _previewToken() { return this._previewGeneration || 0; }

  async fetchPreview(id, pending) {
    const token = this._previewToken;
    const isCurrent = () => token === this._previewToken;
    const result = await this.postReschedule(
      `/app/bookings/${id}/pricing_preview`, "POST", id, pending, { isCurrent }
    );
    if (!isCurrent()) return;
    if (!result) return;
    if (result.kind === "reprice") this.showPending(id, pending, result);
  }

  // A pure move commits silently; anything that moves money waits for the
  // panel's Confirm. See design spec decision 5.
  async commitOrConfirm({ id, next }) {
    // A stale debounced preview (from an earlier, now-superseded drag) must
    // not be allowed to re-open the box after this commit — clear it first.
    this.clearPending();

    const preview = await this.postReschedule(`/app/bookings/${id}/pricing_preview`, "POST", id, next);
    if (!preview) return this.reload();

    if (preview.kind === "pure_move") {
      const result = await this.postReschedule(`/app/bookings/${id}/reschedule`, "PATCH", id, next);
      if (result) await this.refreshInspectorIfOpen(id);
      return this.reload();
    }

    this.pending = { id, next };
    if (this.panelTarget.classList.contains("hidden")) await this.openInspector({ id });
    this.showPending(id, next, preview);
  }

  // Tears down any armed preview state — a debounced preview timer and the
  // pending Confirm box — without reloading the board. Used both when a
  // commit is about to happen and when a drag/resize gesture is abandoned
  // (calendar:drag-cancel).
  clearPending() {
    clearTimeout(this._previewTimer);
    // Supersede any preview already in flight as well as the pending timer.
    this._previewGeneration = this._previewToken + 1;
    this.pending = null;
    if (this.hasPendingBoxTarget) this.pendingBoxTarget.classList.add("hidden");
  }

  // The drag/resize ended back where it started: nothing to commit, but a
  // debounced preview may have already armed the Confirm box for this
  // gesture. Only clear it if it belongs to the abandoned event.
  cancelPending({ id }) {
    if (this.pending && String(this.pending.id) !== String(id)) return;
    this.clearPending();
  }

  showPending(id, next, preview) {
    if (!this.hasPendingBoxTarget) return;

    this.pending = { id, next };
    this.pendingBoxTarget.classList.remove("hidden");
    // A previous failure may have disabled Confirm; this drag is a fresh try.
    if (this.hasConfirmButtonTarget) this.confirmButtonTarget.disabled = false;
    this.pendingSummaryTarget.textContent =
      `${formatLength(preview.original.rentalHours)} → ${formatLength(preview.next.rentalHours)}`;

    const paidNote = preview.paidTotal > 0
      ? ` · ${preview.paidTotal.toLocaleString()} already paid`
      : "";
    this.pendingPriceTarget.textContent =
      `Total ${preview.original.totalPrice.toLocaleString()} → ${preview.next.totalPrice.toLocaleString()}` +
      ` · balance ${preview.next.balanceDue.toLocaleString()}${paidNote}`;
  }

  async confirmPending() {
    if (!this.pending) return;
    const { id, next } = this.pending;
    const result = await this.postReschedule(`/app/bookings/${id}/reschedule`, "PATCH", id, next);
    this.pending = null;
    this.pendingBoxTarget.classList.add("hidden");
    if (result) await this.refreshInspectorIfOpen(id);
    this.reload();
  }

  revertPending() {
    this.pending = null;
    this.pendingBoxTarget.classList.add("hidden");
    this.reload();
  }

  // Every request in this controller goes through here, because the single
  // most dangerous response is one that LOOKS fine. Operator::BaseController
  // answers an expired or foreign session with `redirect_to new_session_path`,
  // and fetch follows redirects — so the sign-in page arrives as a 200 and
  // `response.ok` is true. Parsing it then throws a SyntaxError that nothing
  // catches, and the board keeps rendering a change that was never saved.
  //
  // A 2xx that is not JSON therefore means the session is gone, not success.
  async fetchJson(url, options) {
    let response;
    try {
      response = await fetch(url, options);
    } catch {
      return { ok: false, offline: true, status: 0, body: {} };
    }

    // ensure_member answers a dead session with `redirect_to new_session_path`,
    // never a 401, and fetch FOLLOWS that redirect — so what lands here is the
    // sign-in page. Measured, it arrives as a 406, because the login page
    // cannot render the JSON this request asked for; a page that can render
    // HTML for it would arrive as a 200 instead. Neither status identifies it.
    // The redirect itself is the reliable signal, and this API issues no other.
    if (response.redirected || response.status === 401 || response.status === 403) {
      return { ok: false, signedOut: true, status: response.status, body: {} };
    }

    // Anything else that is not JSON is a fault, not a sign-out — an error page
    // where a payload was expected. Parsing it would throw a SyntaxError that
    // nothing catches, and the board would keep rendering an unsaved change.
    const isJson = (response.headers.get("content-type") || "").includes("json");
    if (!isJson) return { ok: false, notJson: true, status: response.status || 500, body: {} };

    return {
      ok: response.ok,
      status: response.status,
      body: await response.json().catch(() => ({}))
    };
  }

  // Returns the parsed body on success, or null after surfacing the error.
  //
  // `options.isCurrent` lets a caller disown a response that arrived too late:
  // a preview fired mid-gesture can resolve after the operator has abandoned
  // the drag, and announcing its refusal then reports a change nobody is
  // making any more.
  async postReschedule(url, method, id, values, options = {}) {
    const event = this.calendar.events.find((e) => String(e.id) === String(id));

    // lock_version is the ONLY thing standing between two operators editing the
    // same booking, and RescheduleService#stale? reads nil as "this caller
    // isn't participating in locking" — right for internal callers, wrong here.
    // JSON.stringify drops an undefined value entirely, so sending `undefined`
    // would turn the check off rather than fail it, and the write would clobber
    // whatever the other operator just saved. The lookup misses for real: a
    // reload between the drop and Confirm replaces calendar.events.
    if (!event || event.lockVersion === undefined || event.lockVersion === null) {
      this.showError("Lost track of that booking. Reloading the board.");
      this.reload();
      return null;
    }

    const result = await this.fetchJson(url, {
      method,
      headers: {
        "Content-Type": "application/json",
        "X-CSRF-Token": this.csrf(),
        Accept: "application/json"
      },
      body: JSON.stringify({
        reschedule: {
          starts_at: values.start,
          ends_at: values.end,
          vehicle_id: values.resourceId,
          lock_version: event.lockVersion
        }
      })
    });

    if (result.ok) return result.body;

    // Every branch below either tells the operator something or reloads the
    // board. A response the caller has already disowned must do neither: the
    // gesture it belonged to is over, and a reload would fight the next one.
    if (options.isCurrent && !options.isCurrent()) return null;

    if (result.offline) {
      // The request never landed. The bar is sitting at a position the server
      // knows nothing about, so put it back.
      this.showError("Could not reach the server. The change was not saved.");
      this.reload();
      return null;
    }

    if (result.signedOut) {
      this.showError("Your session expired. Sign in again — the change was not saved.");
      this.reload();
      return null;
    }

    // A 5xx is a fault, not a refusal. Saying "could not apply that change"
    // for both makes a crash indistinguishable from a booking that legitimately
    // will not move.
    //
    // An HTML body is the same kind of thing: a Rails error page, or the 422
    // the CSRF check renders once the session is gone (measured — that one does
    // NOT redirect, so it never reaches the signedOut branch above).
    if (result.status >= 500 || result.notJson) {
      this.showError("The change was not saved — the server did not respond properly. Reload the page and try again.");
      this.reload();
      return null;
    }

    const body = result.body;
    const dates = body.conflict
      ? `${body.conflict.startsAt.slice(0, 10)} – ${body.conflict.endsAt.slice(0, 10)}`
      : "";
    // The panel has room for the reference as well; the toast already names
    // the customer in the sentence, so it takes the range alone.
    const detail = body.conflict
      ? `${body.conflict.customer} · ${dates} · ${body.conflict.reference}`
      : "";

    this.showError(body.message || "Could not apply that change.", detail, dates);

    // A stale write means someone else moved it; refetch so the board shows
    // what is actually true before the operator tries again.
    if (body.error === "stale") this.reload();
    return null;
  }

  // A refused drag has to say so. The panel is a good surface when it is open —
  // the conflicting booking's reference belongs beside the form — but its
  // targets live inside a Turbo Frame that is empty until a booking is loaded,
  // and a plain drag never opens it. On its own that left the bar springing
  // back with nothing said but a console warning.
  showError(message, detail = "", brief = "") {
    this.toast(brief ? `${message} ${brief}` : message);

    if (!this.hasPendingBoxTarget) return;

    this.pendingSummaryTarget.textContent = message;
    this.pendingPriceTarget.textContent = detail;
    this.pendingBoxTarget.classList.remove("hidden");
    if (this.hasConfirmButtonTarget) this.confirmButtonTarget.disabled = true;
  }

  // Mirrors shared/_toast.html.erb — built here rather than fetched because
  // the failure is already in hand and a round trip could fail the same way.
  // Stimulus picks the controller up from the inserted node, so it dismisses
  // itself like any other toast.
  toast(message) {
    const host = document.getElementById("flash");
    if (!host) {
      console.warn("[calendar]", message);
      return;
    }

    // A refused drag fails twice — once on the debounced preview while the
    // pointer is still down, once on drop — and repeating the same sentence
    // twice is noise. Restart the existing toast instead of stacking another.
    const existing = [ ...host.querySelectorAll("[data-calendar-toast]") ]
      .find((n) => n.dataset.calendarToast === message);
    if (existing) {
      existing.remove();
    }

    const el = document.createElement("div");
    el.dataset.calendarToast = message;
    el.dataset.controller = "toast";
    // Longer than the default: a conflict names a booking reference the
    // operator may want to go and look at.
    el.dataset.toastTimeoutValue = "7000";
    el.className =
      "pointer-events-auto flex items-start gap-3 bg-white border border-line " +
      "rounded-card shadow-lg px-4 py-3 max-w-sm transition-all duration-300";

    const icon = document.createElementNS("http://www.w3.org/2000/svg", "svg");
    icon.setAttribute("class", "w-5 h-5 text-danger shrink-0 mt-0.5");
    icon.setAttribute("fill", "none");
    icon.setAttribute("viewBox", "0 0 24 24");
    icon.setAttribute("stroke", "currentColor");
    icon.setAttribute("stroke-width", "2");
    const path = document.createElementNS("http://www.w3.org/2000/svg", "path");
    path.setAttribute("stroke-linecap", "round");
    path.setAttribute("stroke-linejoin", "round");
    path.setAttribute("d", "M12 9v4m0 4h.01M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z");
    icon.appendChild(path);

    const text = document.createElement("p");
    text.className = "text-sm text-ink";
    // textContent, not innerHTML: the message carries a server string.
    text.textContent = message;

    const close = document.createElement("button");
    close.type = "button";
    close.dataset.action = "click->toast#dismiss";
    close.className = "ml-auto text-faint hover:text-muted";
    close.setAttribute("aria-label", "Dismiss");
    close.textContent = "\u00d7";

    el.append(icon, text, close);
    host.prepend(el);
  }

  // ---- helpers ----

  _subscribe(name, handler) {
    this.calendar.on(name, handler);
  }

  parseDate(dateString) {
    const [y, m, d] = dateString.split("-").map(Number);
    return new Date(y, m - 1, d);
  }

  format(date) {
    const m = String(date.getMonth() + 1).padStart(2, "0");
    const d = String(date.getDate()).padStart(2, "0");
    return `${date.getFullYear()}-${m}-${d}`;
  }

  mondayOf(date) {
    const d = new Date(date);
    d.setDate(d.getDate() - ((d.getDay() + 6) % 7));
    return d;
  }

  csrf() {
    return document.querySelector("meta[name=csrf-token]")?.content || "";
  }
}
